import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { api } from "../lib/api";
import { useAuth } from "../lib/AuthContext";
import BreathingCircle from "../components/BreathingCircle";
import logoMark from "../assets/logo-mark.png";

const STEPS = [
  {
    title: "Welcome to your Reset.",
    body: "Back Pain Solution is a guided 4-week mind–body program. We move slowly. Honestly. Together.",
    cta: "Begin",
  },
  {
    title: "Where do you feel it most?",
    body: "Choose where pain shows up the most.",
    field: "primary_pain_area",
    options: ["Neck", "Upper back", "Mid back", "Lower back", "Hips", "Whole body"],
  },
  {
    title: "How long have you been carrying this?",
    body: "There are no wrong answers.",
    field: "pain_duration",
    options: ["< 6 months", "6–12 months", "1–3 years", "3+ years"],
  },
  {
    title: "What do you want from this reset?",
    body: "Pick all that resonate.",
    field: "goals",
    multi: true,
    options: ["Less pain", "Emotional calm", "Better posture", "More energy", "Confidence", "Mind–body connection"],
  },
];

export default function Onboarding() {
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [i, setI] = useState(0);
  const [data, setData] = useState({ goals: [] });
  const [saving, setSaving] = useState(false);

  const step = STEPS[i];

  function toggle(field, value, multi) {
    if (multi) {
      setData((d) => {
        const arr = d[field] || [];
        return { ...d, [field]: arr.includes(value) ? arr.filter((v) => v !== value) : [...arr, value] };
      });
    } else {
      setData((d) => ({ ...d, [field]: value }));
    }
  }

  async function next() {
    if (i < STEPS.length - 1) { setI(i + 1); return; }
    setSaving(true);
    try {
      await api.post("/me/onboarding", data);
      await refresh();
      navigate("/home");
    } finally { setSaving(false); }
  }

  const canNext = (() => {
    if (i === 0) return true;
    const f = step.field;
    if (step.multi) return (data[f] || []).length > 0;
    return !!data[f];
  })();

  return (
    <div className="relative min-h-screen px-6 py-10 flex flex-col">
      <div className="bps-ambient" />
      <div className="max-w-md mx-auto w-full flex-1 flex flex-col">
        <div className="flex justify-center mb-4">
          <img
            src={logoMark}
            alt="Back Pain Solution"
            data-testid="onboarding-logo-mark"
            className="w-14 h-14 rounded-2xl object-cover drop-shadow-[0_0_22px_rgba(34,211,238,0.5)] select-none"
            draggable={false}
          />
        </div>
        <div className="flex items-center gap-2 mb-8">
          {STEPS.map((s, idx) => (
            <div key={s.title} className={`h-1 flex-1 rounded-full transition-all ${idx <= i ? "bg-[#22D3EE]" : "bg-white/10"}`} />
          ))}
        </div>

        <AnimatePresence mode="wait">
          <motion.div
            key={i}
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -16 }}
            transition={{ duration: 0.5 }}
            className="flex-1 flex flex-col"
          >
            <div className="bps-eyebrow">Week {i + 1} of {STEPS.length}</div>
            <h1 className="font-display text-3xl sm:text-4xl font-light tracking-tighter mt-2 text-white">{step.title}</h1>
            <p className="text-[#9AAEC6] mt-3">{step.body}</p>

            <div className="mt-8 space-y-3">
              {i === 0 ? (
                <div className="flex justify-center my-8"><BreathingCircle label="Inhale" /></div>
              ) : (
                step.options.map((opt) => {
                  const active = step.multi ? (data[step.field] || []).includes(opt) : data[step.field] === opt;
                  return (
                    <button
                      key={opt}
                      onClick={() => toggle(step.field, opt, step.multi)}
                      data-testid={`onboarding-opt-${opt.toLowerCase().replace(/[^a-z]/g, "-")}`}
                      className={`w-full text-left p-4 rounded-2xl border transition-all ${
                        active ? "border-[#22D3EE] bg-[#22D3EE]/10 text-[#22D3EE]" : "border-white/10 hover:border-[#1C1C1E]/30"
                      }`}
                    >
                      {opt}
                    </button>
                  );
                })
              )}
            </div>
          </motion.div>
        </AnimatePresence>

        <button
          onClick={next}
          disabled={!canNext || saving}
          data-testid="onboarding-next"
          className="mt-8 w-full py-4 rounded-full bg-[#22D3EE] text-[#03101F] font-medium disabled:opacity-30 transition-all hover:-translate-y-0.5"
        >
          {i === STEPS.length - 1 ? (saving ? "Saving…" : "Enter") : step.cta || "Continue"}
        </button>
      </div>
    </div>
  );
}
